import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/cn.js';

export interface OptionCard<T extends string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
}

export interface OptionCardsProps<T extends string> {
  name: string;
  legend: string;
  options: readonly OptionCard<T>[];
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  /** Grille à deux colonnes au-delà de `sm` (choix du type d'opération). */
  columns?: 1 | 2;
}

/**
 * Choix exclusif présenté en cartes (libellé + conséquence en une phrase) :
 * radios natifs, donc clavier et lecteurs d'écran sans code en plus.
 */
export function OptionCards<T extends string>({
  name,
  legend,
  options,
  value,
  onChange,
  disabled = false,
  columns = 1,
}: OptionCardsProps<T>): JSX.Element {
  return (
    <fieldset className="flex min-w-0 flex-col gap-2" disabled={disabled}>
      <legend className="mb-1.5 text-sm font-medium">{legend}</legend>
      <div className={cn('grid gap-2', columns === 2 && 'sm:grid-cols-2')}>
        {options.map((option) => {
          const checked = option.value === value;
          return (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
                checked ? 'border-primary bg-accent' : 'hover:bg-muted/50',
                disabled && 'cursor-not-allowed opacity-70',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                className="mt-0.5 size-4 accent-primary"
                onChange={() => {
                  onChange(option.value);
                }}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-medium">{option.label}</span>
                {option.description ? <span className="text-xs leading-5 text-muted-foreground">{option.description}</span> : null}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
